'use client';

import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';

/* Shared rhythm for the home hero: one timer decides which occasion is
   "in front". The card fan, the signature bubble and the highlighted feature chip
   all follow it. */

export const OCCASIONS = [
  {
    word: 'Birthday',
    card: { src: 'https://images.lovingecards.com/images/cards/happy-birthday-celebration-wishes_3OUSy1i.png', alt: 'Happy birthday celebration wishes group card' },
    message: { text: 'Happy birthday! The office is brighter with you in it 🎉', from: 'Priya' },
    feature: 'photos',
  },
  {
    word: 'Work Anniversary',
    card: { src: 'https://images.lovingecards.com/images/cards/Happy-Work-Anniversary-Card.svg', alt: 'Happy work anniversary group card' },
    message: { text: 'Happy work anniversary! Another year of making the team better 🏆', from: 'Aisha' },
    feature: 'logo',
  },
  {
    word: 'Farewell',
    card: { src: 'https://images.lovingecards.com/images/cards/goodbye-good-luck-farewell-card.png', alt: 'Goodbye and good luck farewell group card' },
    message: { text: 'We’ll miss you so much. Good luck on your next adventure! 👋', from: 'Daniel' },
    feature: 'music',
  },
  {
    word: 'Thank You',
    card: { src: 'https://images.lovingecards.com/images/cards/thank-you-from-all-of-us-typography-card.png', alt: 'Thank you from all of us group card' },
    message: { text: 'Thank you for always going the extra mile. We appreciate you 🙏', from: 'Marco' },
    feature: 'schedule',
  },
] as const;

export type FeatureKey = (typeof OCCASIONS)[number]['feature'] | 'signup';

// Headline words: the default phrase first, then one per card occasion.
const DEFAULT_WORD = 'Every Celebration';
const WORDS = [DEFAULT_WORD, ...OCCASIONS.map((o) => o.word)];

const INTERVAL_MS = 4200; // leaves time to erase, type and read each word

type Ctx = { index: number; word: string; setPaused: (p: boolean) => void };
const HeroOccasionContext = createContext<Ctx>({ index: 0, word: DEFAULT_WORD, setPaused: () => {} });

export function HeroOccasionProvider({ children }: { children: ReactNode }) {
  // step 0 = "Every Celebration" (birthday card in front), steps 1..n = each occasion.
  const [step, setStep] = useState(0);
  const [paused, setPaused] = useState(false);

  useEffect(() => {
    if (paused) return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    const id = setInterval(() => setStep((s) => (s + 1) % WORDS.length), INTERVAL_MS);
    return () => clearInterval(id);
  }, [paused]);

  const index = step === 0 ? 0 : step - 1;
  return <HeroOccasionContext.Provider value={{ index, word: WORDS[step], setPaused }}>{children}</HeroOccasionContext.Provider>;
}

export const useHeroOccasion = () => useContext(HeroOccasionContext);

/** The occasion word in the headline with a typing effect (same font as the
 *  surrounding phrase): the previous word is erased letter by letter, the new
 *  one is typed, then a hand-drawn blush underline draws in beneath it. Server-renders the default phrase, so crawlers see
 *  "Online for Every Celebration". */
export function TypedOccasion() {
  const { word } = useHeroOccasion();
  const [text, setText] = useState(DEFAULT_WORD);

  useEffect(() => {
    if (text === word) return;
    // Erase until the current text is a prefix of the target, then type the rest.
    const typing = word.startsWith(text);
    const t = setTimeout(
      () => setText(typing ? word.slice(0, text.length + 1) : text.slice(0, -1)),
      typing ? 85 : 35,
    );
    return () => clearTimeout(t);
  }, [text, word]);

  return (
    // Every word is stacked in the same grid cell as an invisible spacer (CSS
    // content only, so crawlers don't read it): the box is always as wide as the
    // longest word, typing fills it from the left, and nothing around it moves
    // (no layout shift, and the headline's own text never changes).
    <span className="relative inline-grid text-left align-baseline">
      {WORDS.map((w) => (
        <span key={w} data-w={w} className="invisible col-start-1 row-start-1 whitespace-nowrap before:content-[attr(data-w)]" aria-hidden="true" />
      ))}
      <span className="col-start-1 row-start-1 whitespace-nowrap">
        <span className="relative inline-block">
        {text || ' '}
        {text === word && (
          // key → remount → the underline re-draws for every new word
          <svg
            key={word}
            className="hero-underline pointer-events-none absolute -bottom-[0.14em] left-0 h-[0.3em] w-full text-blush"
            viewBox="0 0 200 12"
            preserveAspectRatio="none"
            aria-hidden="true"
          >
            <path d="M3 8.5C40 3.5 92 2.6 140 5.4C162 6.7 182 8.6 197 7" fill="none" stroke="currentColor" strokeWidth="5" strokeLinecap="round" />
          </svg>
        )}
        </span>
      </span>
    </span>
  );
}
